import useUnallocatedCampaigns from './useUnallocatedCampaigns';
import { VirtualizedTable } from '@round/whitelabel-ui';
import { ColumnDef } from '@tanstack/react-table';
import { Meta, UnallocatedCampaignsRow } from './helpers';
import CampaignCell from './components/CampaignCell/CampaignCell';
import styles from './UnallocatedCampaigns.module.css';
import cn from 'classnames';
import FinanceStatusCell from './components/FinanceStatusCell/FinanceStatusCell';
import PurchaseOrderAllocationModal from 'Modules/Plans/Finance/PurchaseOrders/PurchaseOrderAllocationModal/PurchaseOrderAllocationModal';
import { useCallback, useState } from 'react';
import { isNumber } from 'utility/utility';
import useUrlState from 'Hooks/useUrlState';
import { creatorbase, OrderingValues } from '@round/api';
import NotesCell from './components/NotesCell/NotesCell';
import UnallocatedCampaignsFilters, {
    FilterValues,
} from './components/UnallocatedCampaignsFilters/UnallocatedCampaignsFilters';
import { SkeletonTableCell } from '@round/ui-kit';
import moment from 'moment';
import { mapOrderingToTableSorting, mapTableSortingToOrdering } from '@round/whitelabel-ui';
import DashboardCard from '../DashboardCard/DashboardCard';
import ManageCell from './components/ManageCell/ManageCell';
import ProgressCell from './components/ProgressCell/ProgressCell';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import useEffectWithPreviousDeps from 'Hooks/useEffectWithPreviousDeps';
import { CountRangeLabel } from '@round/whitelabel-ui';
import { useInvalidateInfiniteTeams } from '@round/creatorbase-helpers/api-hooks';

type Props = {
    className?: string;
    teamId: number | null | undefined;
    onTeamChange: (teamId: number | null | undefined) => void;
};

export type UrlState = Omit<FilterValues, 'team_id'> & {
    ordering?: creatorbase.GetCampaignsOrderingKeys;
};

const defaultOrdering: OrderingValues<creatorbase.GetCampaignsOrderingKeys> = '-purchase_orders_unallocated_amount';

const PAGE_SIZE = 25;

const UnallocatedCampaigns = ({ className, teamId, onTeamChange }: Props) => {
    const { selectedClientId } = useClientSelect();

    const [campaignIdToAllocate, setCampaignIdToAllocate] = useState<number | null>(null);
    const [urlState, setUrlState] = useUrlState<UrlState>({});

    useEffectWithPreviousDeps(
        (prev) => {
            const [prevClientId] = prev ?? [];
            if (prevClientId && prevClientId !== selectedClientId) {
                setUrlState({
                    search: '',
                    finance_status: undefined,
                    exclude_fully_allocated: undefined,
                    team_member_id: undefined,
                });
                onTeamChange(null);
            }
        },
        [selectedClientId, setUrlState, onTeamChange]
    );

    const {
        data,
        count,
        pageParams,
        isRefetching,
        isFetching,
        isPending,
        fetchNextPage,
        hasNextPage,
        getIsProjectLoading,
        getIsBrandLoading,
        getArePurchaseOrdersLoading,
        invalidate,
        setCampaign,
        refreshCampaign,
    } = useUnallocatedCampaigns(
        {
            finance_status: urlState?.finance_status as creatorbase.FinanceStatus,
            ordering: [urlState?.ordering, defaultOrdering].filter((v) => !!v).toString(),
            search: urlState.search,
            team_id: teamId ?? undefined,
            team_member_id: urlState.team_member_id,
            exclude_fully_allocated: urlState.exclude_fully_allocated === 'true',
            page_size: PAGE_SIZE,
            client_id: selectedClientId,
        },
        {
            enabled: typeof selectedClientId === 'number',
            placeholderData: (data) => data,
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const columns: ColumnDef<UnallocatedCampaignsRow, any>[] = [
        {
            header: 'Campaign',
            id: 'campaign',
            meta: {
                className: styles.campaignColumn,
            },
            cell: CampaignCell,
        },
        {
            header: 'Progress',
            id: 'progress',
            cell: ProgressCell,
        },
        {
            header: 'Notes',
            id: 'notes',
            accessorKey: 'notes',
            meta: {
                className: styles.notesColumn,
            },
            cell: NotesCell,
        },
        {
            header: 'Status',
            id: 'finance_status',
            accessorKey: 'finance_status',
            meta: {
                className: styles.statusColumn,
            },
            cell: FinanceStatusCell,
        },
        {
            header: 'Start Date',
            id: 'start_date',
            enableSorting: true,
            accessorFn: (row) => (row.start_date ? moment(row.start_date).format('DD MMM YYYY') : '-'),
            meta: {
                getIsCellLoading: (props) => Object.keys(props.row.original).length === 0,
            },
            cell: SkeletonTableCell,
        },
        {
            header: 'Last Updated',
            id: 'finance_last_updated',
            enableSorting: true,
            accessorFn: (row) =>
                row.finance_last_updated ? moment(row.finance_last_updated).format('DD MMM YYYY') : '-',
            meta: {
                getIsCellLoading: (props) => Object.keys(props.row.original).length === 0,
            },
            cell: SkeletonTableCell,
        },
        {
            header: 'Allocated',
            id: 'allocated',
            meta: {
                className: styles.manageColumn,
            },
            cell: ManageCell,
        },
    ];

    const invalidateTeams = useInvalidateInfiniteTeams();
    const invalidateInactiveCampaigns = () => {
        invalidate({ type: 'inactive' });
    };

    const meta: Meta = {
        isLoading: isPending,
        isRefetching: isRefetching,
        getIsBrandLoading,
        getIsProjectLoading,
        getArePurchaseOrdersLoading,
        onAllocate: (row) => {
            setCampaignIdToAllocate(row.id);
        },
        onUpdated: (data) => {
            invalidateInactiveCampaigns();
            if (data) {
                setCampaign(data);
            }
        },
    };

    const rows = !data?.length && isFetching ? new Array(10).fill({}) : data;
    return (
        <>
            <DashboardCard className={cn(styles.container, className)}>
                <div className={styles.header}>
                    <DashboardCard.Title>Unallocated campaigns</DashboardCard.Title>
                    <CountRangeLabel
                        pageParams={pageParams as number[]}
                        count={count}
                        pageSize={PAGE_SIZE}
                        isLoading={isPending}
                    />
                </div>
                <UnallocatedCampaignsFilters
                    // Remount on client change so the team-members Select drops the previous client's selection.
                    key={selectedClientId}
                    filters={{ ...urlState, team_id: teamId?.toString() }}
                    onFiltersChange={(filters) => {
                        setUrlState(filters);
                        if ('team_id' in filters) {
                            onTeamChange(filters.team_id ? Number(filters.team_id) : null);
                        }
                    }}
                />

                <VirtualizedTable
                    isLoading={isFetching}
                    containerClassName={styles.tableContainer}
                    className={styles.table}
                    meta={meta}
                    data={rows}
                    columns={columns}
                    onEndReached={() => {
                        if (!isFetching && hasNextPage) {
                            fetchNextPage();
                        }
                    }}
                    enableSorting
                    manualSorting
                    sorting={mapOrderingToTableSorting(urlState.ordering ? [urlState.ordering] : [])}
                    onSortingChange={(sorting) => {
                        setUrlState({
                            ordering: mapTableSortingToOrdering(sorting)[0] as
                                | creatorbase.GetCampaignsOrderingKeys
                                | undefined,
                        });
                    }}
                    virtualizerOptions={{
                        count: rows.length,
                        estimateSize: () => 79,
                        getItemKey: useCallback((index: number) => data[index]?.id, [data]),
                        overscan: PAGE_SIZE,
                    }}
                />
            </DashboardCard>

            <PurchaseOrderAllocationModal
                campaignId={campaignIdToAllocate}
                isOpen={isNumber(campaignIdToAllocate)}
                onClose={(hasUpdated) => {
                    setCampaignIdToAllocate(null);
                    if (hasUpdated && campaignIdToAllocate) {
                        invalidateInactiveCampaigns();
                        invalidateTeams({ include_credit_breakdown: true }, { type: 'active' });
                        return refreshCampaign(campaignIdToAllocate);
                    }
                }}
            />
        </>
    );
};

export default UnallocatedCampaigns;
